import React, { useEffect, useRef, useState } from 'react';
import { Trophy, Crown, AlertCircle, Award, RefreshCw } from 'lucide-react';
import { fetchPreviousLeaderboard } from '../hooks/useLeaderboard';
import { RankBadge } from './RankBadge';
import Countdown from './Countdown';
import { SITE, PRIZE_SPLIT, DISCLOSURE } from '../mock';
import { useReveal } from '../hooks/useReveal';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const prizeFor = (rank) => (PRIZE_SPLIT.find((p) => p.rank === rank) || {}).prize || 0;

const avatarColor = (seed) => {
  const colors = ['#00D9FF', '#00D9FF', '#7c5cff', '#38d39f', '#ff6b9d', '#ff9f43'];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = seed.charCodeAt(i) + ((h << 5) - h);
  return colors[Math.abs(h) % colors.length];
};

const RANKS = {
  1: { c: '#FACC15', glow: 'rgba(250,204,21,0.45)', tint: 'rgba(250,204,21,0.10)', label: '1ST', Icon: Crown, order: 'order-1 md:order-2', lift: 'md:mt-0', pad: 'md:py-9' },
  2: { c: '#E5E7EB', glow: 'rgba(229,231,235,0.30)', tint: 'rgba(229,231,235,0.07)', label: '2ND', Icon: Trophy, order: 'order-2 md:order-1', lift: 'md:mt-10', pad: '' },
  3: { c: '#F97316', glow: 'rgba(249,115,22,0.40)', tint: 'rgba(249,115,22,0.09)', label: '3RD', Icon: Award, order: 'order-3 md:order-3', lift: 'md:mt-16', pad: '' },
};

function Podium({ entry, rank, leaderWager, run }) {
  if (!entry) return null;
  const s = RANKS[rank];
  const pct = leaderWager ? Math.min(100, Math.max(8, (entry.weightedWagered / leaderWager) * 100)) : 0;
  return (
    <div
      data-testid={`podium-rank-${rank}`}
      className={`relative rounded-2xl px-6 py-7 flex flex-col items-center text-center transition-transform duration-300 hover:-translate-y-1 ${s.lift} ${s.pad} ${s.order}`}
      style={{
        background: `linear-gradient(180deg, ${s.tint} 0%, rgba(10,14,26,0.92) 55%, rgba(8,11,18,0.96) 100%)`,
        border: `1.5px solid ${s.c}${rank === 1 ? 'cc' : '88'}`,
        boxShadow: `0 0 ${rank === 1 ? 46 : 30}px -8px ${s.glow}, inset 0 0 30px -18px ${s.glow}`,
      }}
    >
      <div className="relative mb-5">
        <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${s.c}55` }}>
          <s.Icon size={26} style={{ color: s.c, filter: `drop-shadow(0 0 8px ${s.glow})` }} />
        </div>
        <span className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full text-[10px] font-display font-bold tracking-wide" style={{ background: s.c, color: '#0a0e1a' }}>{s.label}</span>
      </div>
      <div className="flex items-center justify-center gap-2 mt-1 mb-2 max-w-full">
        <RankBadge src={entry.rankLevelImage} size={28} testId={`podium-badge-${rank}`} />
        <h3 data-testid={`podium-name-${rank}`} className="font-display font-bold text-xl tracking-wide truncate text-white">{entry.username}</h3>
      </div>
      <span className="text-[10px] tracking-[0.22em] text-muted font-tech">WAGERED</span>
      <span data-testid={`podium-wager-${rank}`} className={`font-display font-bold ${rank === 1 ? 'text-3xl' : 'text-2xl'}`} style={{ color: s.c, textShadow: `0 0 18px ${s.glow}` }}>${fmt(entry.weightedWagered)}</span>
      <span data-testid={`podium-unweighted-${rank}`} className="text-xs text-muted tabular-nums mt-0.5">${fmt(entry.wagered)} unweighted</span>
      <div className="w-full h-1.5 rounded-full mt-3 mb-6 overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
        <div className="h-full rounded-full transition-all duration-1000" style={{ width: run ? `${pct}%` : '0%', background: s.c, boxShadow: `0 0 12px ${s.glow}` }} />
      </div>
      <span data-testid={`podium-prize-${rank}`} className="px-5 py-2 rounded-full font-display font-bold text-lg" style={{ color: '#4ADE80', background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(74,222,128,0.45)', boxShadow: '0 0 22px -6px rgba(74,222,128,0.6)' }}>${prizeFor(rank).toLocaleString('en-US')}</span>
    </div>
  );
}

const fmtUtc = (iso) => new Date(iso).toLocaleString('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const fmtTime = (iso) => new Date(iso).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

function Rows({ rows, startRank, leaderWager, run }) {
  return (
    <div className="flex flex-col gap-2" data-testid="leaderboard-rows">
      {rows.map((e, i) => {
        const rank = i + startRank;
        const prize = prizeFor(rank);
        const paid = prize > 0;
        const color = avatarColor(e.username || String(rank));
        const pct = leaderWager ? Math.min(100, Math.max(4, (e.weightedWagered / leaderWager) * 100)) : 0;
        return (
          <div key={rank} data-testid={`leaderboard-row-${rank}`}
            className="flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 rounded-xl transition-colors hover:bg-[rgba(0,217,255,0.05)]"
            style={paid ? { background: 'rgba(0,217,255,0.07)', border: '1px solid rgba(0,217,255,0.35)' } : { background: 'rgba(255,255,255,0.015)', border: '1px solid transparent' }}>
            <span className="font-display font-bold text-base sm:text-lg w-6 sm:w-7 text-center flex-shrink-0" style={{ color: paid ? 'var(--ar-cyan)' : 'var(--ar-muted)' }}>{rank}</span>
            <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center flex-shrink-0">
              <RankBadge src={e.rankLevelImage} size={32} testId={`row-badge-${rank}`} />
            </div>
            <div className="flex-1 min-w-0">
              <span className="font-display font-semibold truncate block text-sm sm:text-base">{e.username}</span>
              <span className="sm:hidden block text-xs text-cyan font-tech font-semibold tabular-nums mt-0.5">${fmt(e.weightedWagered)} <span className="text-[10px] text-muted font-normal">weighted</span></span>
              <span className="sm:hidden block text-[10px] text-muted tabular-nums">${fmt(e.wagered)} unweighted</span>
              <div className="hidden sm:block h-1 rounded-full mt-1.5 overflow-hidden max-w-[220px]" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <div className="h-full rounded-full transition-all duration-1000" style={{ width: run ? `${pct}%` : '0%', background: color }} />
              </div>
            </div>
            <div className="hidden sm:block text-right whitespace-nowrap flex-shrink-0">
              <span className="block text-xs sm:text-sm text-cyan font-tech font-semibold tabular-nums">${fmt(e.weightedWagered)} <span className="text-[10px] text-muted font-normal">weighted</span></span>
              <span className="block text-[10px] sm:text-xs text-muted tabular-nums">${fmt(e.wagered)} unweighted</span>
            </div>
            <div className="w-14 sm:w-20 text-right flex-shrink-0">
              {paid ? <span className="inline-block px-2 sm:px-3 py-1 rounded-full font-display font-bold text-xs sm:text-sm" style={{ color: '#4ADE80', background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(74,222,128,0.4)' }}>${prize}</span>
                : <span className="font-display font-bold text-muted" aria-label="No prize">—</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const Skeleton = () => (
  <div data-testid="leaderboard-skeleton" aria-busy="true" aria-label="Loading leaderboard">
    <div className="grid md:grid-cols-3 gap-5 mb-8">{[0, 1, 2].map((i) => <div key={i} className="ar-skel h-64 rounded-2xl" />)}</div>
    <div className="flex flex-col gap-2">{[0, 1, 2, 3].map((i) => <div key={i} className="ar-skel h-14 rounded-xl" />)}</div>
  </div>
);

function RaceHeader({ lb }) {
  const { meta, status, lastSuccessAt, retry, retrying } = lb;
  return (
    <div className="ar-panel inline-flex flex-col items-center px-6 sm:px-10 py-6 gap-4 max-w-full" data-testid="race-header">
      <span data-testid="race-month" className="ar-eyebrow">{meta?.raceMonth ? `${meta.raceMonth} Race` : 'Monthly Race'}</span>
      <div className="font-display font-bold text-4xl sm:text-5xl md:text-6xl text-cyan leading-none" data-testid="prize-pool">${SITE.prizePool}</div>
      <span className="text-sm text-muted -mt-1">Total monthly prize pool · shared by the top 5</span>
      {meta?.startDate ? (
        <p className="text-xs text-muted font-tech" data-testid="race-dates">{fmtUtc(meta.startDate)} – {fmtUtc(meta.endDate)} (UTC)</p>
      ) : <div className="ar-skel h-4 w-64" />}
      <div className="ar-divider w-full" />
      <span className="ar-eyebrow">Ends in</span>
      {meta?.endDate ? <Countdown end={meta.endDate} /> : <div className="ar-skel h-16 w-72" />}
      <div className="ar-divider w-full" />
      <div className="flex flex-wrap items-center justify-center gap-2 text-xs text-muted font-display" data-testid="leaderboard-status">
        {status === 'loading' && 'Loading standings…'}
        {status === 'ok' && <><span className="w-2 h-2 rounded-full bg-cyan ar-pulse" aria-hidden="true" /> Live · updates about every 60s · Last updated {lastSuccessAt ? fmtTime(lastSuccessAt) : '—'}</>}
        {status === 'stale' && <span className="text-[#fbbf24]" data-testid="leaderboard-stale">Standings may be out of date · last successful update {lastSuccessAt ? fmtTime(lastSuccessAt) : 'unknown'}</span>}
        {status === 'unavailable' && <span>Standings unavailable right now.</span>}
        {(status === 'stale' || status === 'unavailable') && (
          <button type="button" onClick={retry} disabled={retrying} data-testid="leaderboard-retry-btn" className="inline-flex items-center gap-1.5 text-cyan font-bold border border-[var(--ar-border-cyan)] rounded-lg px-2.5 py-1 hover:bg-[rgba(0,217,255,0.1)] disabled:opacity-50">
            <RefreshCw size={12} className={retrying ? 'animate-spin' : ''} aria-hidden="true" /> {retrying ? 'Retrying…' : 'Retry'}
          </button>
        )}
      </div>
    </div>
  );
}

function Standings({ list, run }) {
  if (!list.length) return null;
  const top3 = list.slice(0, 3);
  const leaderWager = Math.max(0, ...list.map((e) => e.weightedWagered || 0));
  return (
    <>
      <div data-testid="leaderboard-podium" className="grid md:grid-cols-3 gap-5 md:gap-6 items-start mb-8">
        <Podium entry={top3[1]} rank={2} leaderWager={leaderWager} run={run} />
        <Podium entry={top3[0]} rank={1} leaderWager={leaderWager} run={run} />
        <Podium entry={top3[2]} rank={3} leaderWager={leaderWager} run={run} />
      </div>
      {list.length > 3 && (
        <div className="ar-panel p-2 md:p-4">
          <div className="px-4 py-3 font-display font-bold tracking-wide">RANKS 4 – {list.length}</div>
          <Rows rows={list.slice(3)} startRank={4} leaderWager={leaderWager} run={run} />
        </div>
      )}
    </>
  );
}

export default function Leaderboard({ lb }) {
  useReveal();
  const [run, setRun] = useState(false);
  const [tab, setTab] = useState('current');
  const [prev, setPrev] = useState(null);
  const sectionRef = useRef(null);
  const { status, entries } = lb;

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setRun(true); }, { threshold: 0.1 });
    if (sectionRef.current) obs.observe(sectionRef.current);
    fetchPreviousLeaderboard().then((d) => d.status === 'ok' && d.entries?.length && setPrev(d)).catch(() => {});
    return () => obs.disconnect();
  }, []);

  const showPrev = tab === 'previous' && prev;
  const list = showPrev ? prev.entries : entries;

  return (
    <section id="leaderboard" ref={sectionRef} className="py-16 sm:py-24 relative">
      <div className="ar-container">
        <div className="text-center mb-8 sm:mb-10 reveal">
          <p className="ar-eyebrow mb-3">The Race</p>
          <h2 className="ar-heading text-3xl sm:text-4xl md:text-5xl mb-6">Monthly <span className="text-cyan">Leaderboard</span></h2>
          <RaceHeader lb={lb} />
        </div>

        {prev && (
          <div className="flex justify-center gap-2 mb-8" role="tablist" aria-label="Leaderboard period">
            {[['current', 'Current Race'], ['previous', `${prev.raceMonth} Results`]].map(([k, label]) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`leaderboard-tab-${k}`}
                className={`px-4 py-2 rounded-full text-sm font-display font-bold transition-colors ${tab === k ? 'bg-[var(--ar-cyan)] text-[#05222b]' : 'text-muted border border-[var(--ar-border)] hover:text-cyan'}`}>{label}</button>
            ))}
          </div>
        )}

        <p className="text-xs text-muted text-center mb-8" data-testid="leaderboard-weighted-note">Ranking and prizes are based on weighted wagers. <a href="#leaderboard-rules" className="text-cyan hover:underline">How weighting works</a></p>

        {showPrev && <p className="text-center text-xs text-muted mb-6" data-testid="previous-race-note">Closed race · {fmtUtc(prev.startDate)} – {fmtUtc(prev.endDate)} (UTC)</p>}

        {!showPrev && status === 'loading' && <Skeleton />}

        {!showPrev && status === 'unavailable' && (
          <div className="ar-panel p-14 text-center" data-testid="leaderboard-unavailable">
            <AlertCircle size={40} className="text-cyan mx-auto mb-4" aria-hidden="true" />
            <h3 className="font-display font-bold text-xl mb-2">Leaderboard Unavailable</h3>
            <p className="text-muted max-w-md mx-auto mb-5">We couldn't reach the Roobet stats service. Standings will appear automatically once it's back.</p>
            <button type="button" onClick={lb.retry} className="btn-gold" data-testid="leaderboard-unavailable-retry-btn"><RefreshCw size={16} aria-hidden="true" /> TRY AGAIN</button>
          </div>
        )}

        {(showPrev || status === 'ok' || status === 'stale') && <Standings list={list} run={run} />}

        {!showPrev && (status === 'ok' || status === 'stale') && entries.length === 0 && (
          <div className="ar-panel p-14 text-center" data-testid="leaderboard-empty">
            <Trophy size={40} className="text-cyan mx-auto mb-4" aria-hidden="true" />
            <h3 className="font-display font-bold text-xl mb-2">No Wagers Yet</h3>
            <p className="text-muted">Be the first to climb the board — play with code {SITE.code}.</p>
          </div>
        )}

        <div className="mt-8 text-center" id="leaderboard-rules">
          <a href={SITE.roobetRef} target="_blank" rel="noopener noreferrer" className="btn-gold mb-7" data-testid="leaderboard-join-roobet-btn"><img src="/assets/roobet-chip-navy.png" alt="" aria-hidden="true" className="h-6 w-6 object-contain" /> JOIN THE RACE ON ROOBET</a>
          <div className="ar-panel p-5 max-w-3xl mx-auto text-left">
            <h3 className="font-display font-bold text-sm mb-2">Counting Rules</h3>
            <p className="text-xs leading-relaxed text-muted font-tech whitespace-pre-line">{DISCLOSURE}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
